import { Clarity } from './clarity';

const BRITISH_COUNCIL_HOST = 'britishcouncil.org';
const ANALYTICS_GROUP_ID = 'C0002';

type OneTrustWindow = Window & {
  OnetrustActiveGroups?: string | string[];
  OptanonActiveGroups?: string | string[];
};

type ConsentCallback = () => void;
const defaultOneTrustWindow = globalThis as unknown as OneTrustWindow;

const normalizeGroups = (groups: string | string[] | undefined): string[] => {
  if (!groups) {
    return [];
  }

  if (Array.isArray(groups)) {
    return groups;
  }

  return groups
    .split(',')
    .map((group) => group.trim())
    .filter(Boolean);
};

export const isBritishCouncilHost = (host: string) => {
  const h = host.split(':')[0].toLowerCase();
  return h === BRITISH_COUNCIL_HOST || h.endsWith('.' + BRITISH_COUNCIL_HOST);
};

export const getActiveOneTrustGroups = (
  win: OneTrustWindow = defaultOneTrustWindow
): string[] => {
  return normalizeGroups(win.OnetrustActiveGroups ?? win.OptanonActiveGroups);
};

export const hasAnalyticsConsent = (groups: string[]) => groups.includes(ANALYTICS_GROUP_ID);

export const syncWithAnalyticsConsent = (
  onGranted: ConsentCallback,
  onDenied: ConsentCallback,
  win: OneTrustWindow = defaultOneTrustWindow
): () => void => {
  if (!isBritishCouncilHost(win.location.host)) {
    onGranted();
    return () => {};
  }

  const applyConsent = (groups?: string[]) => {
    if (hasAnalyticsConsent(groups ?? getActiveOneTrustGroups(win))) {
      onGranted();
      return;
    }

    onDenied();
  };

  const onGroupsUpdated = (event: Event) => {
    const details =
      event instanceof CustomEvent && Array.isArray(event.detail) ? event.detail : undefined;
    applyConsent(details);
  };

  win.addEventListener('OneTrustGroupsUpdated', onGroupsUpdated);
  applyConsent();

  return () => {
    win.removeEventListener('OneTrustGroupsUpdated', onGroupsUpdated);
  };
};

export const syncClarityWithAnalyticsConsent = (
  projectId: string,
  win: OneTrustWindow = defaultOneTrustWindow
): () => void => {
  if (!projectId) {
    return () => {};
  }

  return syncWithAnalyticsConsent(
    () => {
      Clarity.enable(projectId);
    },
    () => {
      Clarity.disable();
    },
    win
  );
};
